/**
 * Which dropdown option a stored value stands for. A saved application can
 * hold an option's id ("9982") or its name ("9 m", "No"), so the value is
 * matched the same way the dropdown matches it when the form opens
 * (RenderSelect in components/RenderField/RenderField.tsx):
 *
 *  1. an option whose id or name is exactly the value (case and spaces
 *     ignored) wins;
 *  2. only when there is none, an option whose name contains the value, or
 *     is contained in it, is used (both longer than 2 characters, so "No"
 *     never partly matches anything).
 *
 * Both the dropdown and the reviewer's "Your changes" list use this, so the
 * list never counts a value as changed only because the dropdown swapped a
 * stored name for its option id. Returns undefined for a blank value or one
 * that matches no option.
 */
export type TSelectOptionLike = {
  id: number | string | boolean;
  name: string;
};

export function matchSelectOption<T extends TSelectOptionLike>(
  options: T[] | undefined,
  value: unknown
): T | undefined {
  if (value === undefined || value === null || !options?.length) return undefined;
  const valStr = String(value).trim().toLowerCase();
  if (!valStr) return undefined;

  const optionText = (opt: T) => ({
    id: String(opt.id ?? "").trim().toLowerCase(),
    name: String(opt.name ?? "").trim().toLowerCase(),
  });

  return (
    options.find((opt) => {
      const { id, name } = optionText(opt);
      return id === valStr || name === valStr;
    }) ||
    options.find((opt) => {
      const { name } = optionText(opt);
      return (
        (valStr.length > 2 && name.includes(valStr)) ||
        (name.length > 2 && valStr.includes(name))
      );
    })
  );
}
